import { VoiceCommandSession } from '../core/voice-session.js';
import { MicrophoneLevel } from './microphone-level.js';
import { audioSession } from './audio-session.js';

export class VoiceControl {
  constructor(onCommand, onState, getSettings, getTime, onLevel = () => {}, onPhase = () => {}) {
    Object.assign(this, { onCommand, onState, getSettings, getTime });
    this.active = false; this.retries = 0; this.token = 0;
    this.meter = new MicrophoneLevel(onLevel);
    this.commands = new VoiceCommandSession(onCommand, onPhase, () => this.getSettings().prefix);
    this.localLanguages = new Map(); this.prepareLocalRecognition();
  }
  get available() { return !!(window.SpeechRecognition || window.webkitSpeechRecognition); }
  prepareLocalRecognition() {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition, language = this.getSettings().language;
    if (typeof Recognition?.available !== 'function' || this.localLanguages.has(language)) return;
    this.localLanguages.set(language, 'checking');
    try {
      Promise.resolve(Recognition.available({ langs: [language], processLocally: true }))
        .then(status => this.localLanguages.set(language, status)).catch(() => this.localLanguages.set(language, 'unavailable'));
    } catch { this.localLanguages.set(language, 'unavailable'); }
  }
  watchStartup(token) {
    clearTimeout(this.startDeadline);
    this.startDeadline = setTimeout(() => {
      if (!this.active || token !== this.token) return;
      this.stop(); this.onState({ state: 'error', message: 'The browser speech engine did not start. Try Voice again; if this continues, open this page in Safari or Chrome.' });
    }, 12000);
  }
  async start() {
    if (this.active) return;
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) { this.onState({ state: 'error', message: 'Voice is unavailable in this browser. Try Safari on iPad or Chrome on Android.' }); return; }
    const token = ++this.token;
    this.active = true; this.retries = 0;
    this.commands.cancel();
    this.onState({ state: 'starting', message: 'Connecting microphone…' });
    let recognition;
    try { recognition = new Recognition(); }
    catch (error) { this.stop(); this.onState({ state: 'error', message: `Speech recognition could not start: ${error.message}` }); return; }
    this.recognition = recognition;
    recognition.lang = this.getSettings().language;
    recognition.continuous = true; recognition.interimResults = true;
    let local = false;
    // Probe language packs ahead of time: never await an experimental API between
    // the user's click and start(), which must retain that browser activation.
    if ('processLocally' in recognition && this.localLanguages.get(recognition.lang) === 'available') { recognition.processLocally = true; local = true; }
    this.prepareLocalRecognition();
    recognition.onstart = () => { if (!this.active || token !== this.token) return; clearTimeout(this.startDeadline); this.startedAt = performance.now(); this.onState({ state: 'listening', message: local ? 'Listening · on device' : 'Listening · browser speech service' }); };
    recognition.onspeechstart = () => { this.speechTime = this.getTime(); };
    recognition.onresult = event => {
      if (!this.active || token !== this.token) return;
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i], transcript = result[0].transcript;
        this.onState({ state: 'listening', message: local ? 'Listening · on device' : 'Listening · browser speech service', transcript });
        this.commands.consume(transcript, result.isFinal, this.speechTime);
        if (result.isFinal) this.speechTime = undefined;
      }
    };
    recognition.onerror = event => {
      if (!this.active || token !== this.token) return;
      if (event.error === 'no-speech') { this.commands.finishUnmatched(); return; }
      if (event.error === 'aborted') { this.commands.cancel(); return; }
      const messages = {
        'not-allowed': 'Microphone permission was denied. Enable it in your browser settings.',
        'service-not-allowed': 'Speech recognition is disabled. On iPad, check Siri and dictation settings.',
        'audio-capture': 'No available microphone was found.',
        network: 'The browser speech service could not connect. Camera and touch controls still work.',
        'language-not-supported': 'English speech recognition is unavailable on this device.',
      };
      this.stop(); this.onState({ state: 'error', message: messages[event.error] || `Voice stopped: ${event.error}.` });
    };
    recognition.onend = () => {
      if (!this.active || token !== this.token) return;
      clearTimeout(this.startDeadline);
      this.commands.finishUnmatched();
      if (performance.now() - (this.startedAt || 0) > 10000) this.retries = 0;
      if (++this.retries > 4) { this.stop(); this.onState({ state: 'error', message: 'Speech recognition stopped repeatedly. Tap Voice to reconnect.' }); return; }
      clearTimeout(this.timer);
      this.timer = setTimeout(() => { if (this.active && token === this.token) { try { this.watchStartup(token); recognition.start(); } catch { this.stop(); this.onState({ state: 'error', message: 'Tap Voice to reconnect the microphone.' }); } } }, Math.min(500 * this.retries, 2000));
    };
    recognition.onnomatch = () => { if (this.active && token === this.token) this.commands.finishUnmatched(); };
    try {
      this.releaseCapture = audioSession.beginCapture();
      this.watchStartup(token); recognition.start();
      if (this.active && token === this.token) this.meter.start();
    }
    catch (error) { this.stop(); this.onState({ state: 'error', message: error.message }); }
  }
  stop() {
    this.active = false; ++this.token; clearTimeout(this.timer); clearTimeout(this.startDeadline);
    this.commands.cancel(); this.speechTime = undefined;
    this.meter.stop();
    const releaseCapture = this.releaseCapture; this.releaseCapture = null;
    if (this.recognition) {
      // abort() returns before Safari releases capture. Wait for that native
      // acknowledgement before selecting playback, with a bound for engines
      // that fail to emit end after a failed start. The lease belongs only to
      // this recognition, so a late end cannot release a newer session.
      const recognition = this.recognition; this.recognition = null;
      const finish = () => { clearTimeout(timer); releaseCapture?.(); };
      const timer = setTimeout(finish, 1500);
      recognition.onend = finish;
      try { recognition.abort(); } catch { finish(); }
    } else releaseCapture?.();
    this.onState({ state: 'off', message: 'Voice is off' });
  }
}
